import { Link } from 'react-router-dom'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { useAuth, useApp } from '../components/AppContext'
import { calcTax, fmt, mileageAllowance, homeOfficeAllowance, getCurrentQuarter } from '../lib/tax'
import { MONTHLY_DATA, CATEGORIES } from '../data/mockData'
import QuarterlyScore from '../components/QuarterlyScore'
import { AlertCircle, ChevronRight, CheckCircle, ArrowRight, Lock, AlertTriangle, Info } from 'lucide-react'

export default function Dashboard() {
  const { profile } = useAuth()
  const { transactions, mileageTrips, homeOffice, lockedPeriods } = useApp()
  const quarter = getCurrentQuarter()

  const income   = transactions.filter(t => t.type === 'income'  && t.category && t.category !== 'personal').reduce((s,t) => s + t.amount, 0)
  const expenses = transactions.filter(t => t.type === 'expense' && t.category && t.category !== 'personal').reduce((s,t) => s + t.amount, 0)
  const miles    = mileageTrips.reduce((s,t) => s + Number(t.miles), 0)
  const mileAdj  = mileageAllowance(miles)
  const homeAdj  = homeOfficeAllowance(homeOffice.months, homeOffice.hoursPerMonth)
  const totalExp = expenses + mileAdj + homeAdj
  const { incomeTax, ni, total } = calcTax(income * 4, totalExp * 4)
  const monthly  = total / 12

  const allTx      = transactions
  const uncatCount = allTx.filter(t => !t.category).length
  const receiptsUp = allTx.filter(t => t.has_vat_receipt).length
  const expCount   = allTx.filter(t => t.type === 'expense').length

  const name     = profile?.full_name?.split(' ')[0] || 'there'
  const isDemo   = !profile?.bank_connected
  const hour     = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  const primaryAction = uncatCount > 0
    ? { icon: AlertCircle, color: 'var(--amber)', label: `${uncatCount} transactions need categorising`, sub: 'Your tax estimate is incomplete until these are sorted', link: '/categorise', cta: 'Sort now', urgency: 'high' }
    : uncatCount === 0 && receiptsUp < expCount * 0.5
    ? { icon: AlertTriangle, color: 'var(--slate)', label: `${expCount - receiptsUp} expenses missing receipts`, sub: 'Keep receipts to back up your expense claims', link: '/receipts', cta: 'Upload receipts', urgency: 'medium' }
    : { icon: CheckCircle, color: 'var(--teal)', label: `${quarter.label} is ready to review`, sub: `Deadline: ${quarter.deadline}`, link: '/submit', cta: 'Review and submit', urgency: 'low' }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
      <div style={{ background:'var(--navy-card)', border:'1px solid var(--navy-border)', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
        <div style={{ color:'var(--slate)', marginBottom:4 }}>{label}</div>
        <div style={{ color:'var(--teal)' }}>Income: {fmt(payload[0]?.value)}</div>
        <div style={{ color:'var(--slate)' }}>Expenses: {fmt(payload[1]?.value)}</div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="page-head fade-up" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8, marginBottom:20 }}>
        <div>
          <h1 className="page-title">{greeting}, {name}</h1>
          <p className="page-sub">
            {quarter.label} 2026/27 — deadline <strong style={{color:'var(--white)'}}>{quarter.deadline}</strong>
          </p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {lockedPeriods?.length > 0 && (
            <span className="badge badge-slate" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <Lock size={10} /> {lockedPeriods.length} locked
            </span>
          )}
          <span className="badge badge-teal">Live</span>
        </div>
      </div>

      {/* Demo banner */}
      {isDemo && (
        <div className="fade-up" style={{ background:'linear-gradient(135deg,rgba(0,212,170,.07),rgba(0,212,170,.02))', border:'1px solid rgba(0,212,170,.25)', borderRadius:'var(--r)', padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Info size={14} color="var(--teal)" style={{ flexShrink:0 }} />
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--white)', marginBottom:1 }}>You're viewing sample data</div>
              <div style={{ fontSize:12, color:'var(--slate)' }}>Upload your bank statement to see your real tax position</div>
            </div>
          </div>
          <Link to="/connect" className="btn btn-primary btn-sm" style={{ whiteSpace:'nowrap', flexShrink:0 }}>
            Add transactions →
          </Link>
        </div>
      )}

      {/* Getting started steps */}
      {isDemo && (
        <div className="fade-up" style={{ border:'1px solid var(--navy-border)', borderRadius:'var(--r)', padding:'18px 20px', marginBottom:20, background:'var(--navy-card)' }}>
          <div style={{ fontSize:14, fontWeight:600, color:'var(--white)', marginBottom:3 }}>Get started in 3 steps</div>
          <div style={{ fontSize:12, color:'var(--slate)', marginBottom:16 }}>Your real tax position in under 5 minutes</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[
              { n:1, label:'Upload your bank statement', sub:'Download CSV from your bank app — all UK banks supported', link:'/connect' },
              { n:2, label:'Categorise your transactions', sub:'Quarterly AI handles most of it automatically', link:'/categorise' },
              { n:3, label:'See your live tax estimate', sub:'Updates every time you categorise a transaction', link:'/dashboard' },
            ].map(step => (
              <Link key={step.n} to={step.link} style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:'rgba(255,255,255,.02)', borderRadius:8, border:'1px solid rgba(255,255,255,.05)' }}>
                <div style={{ width:26, height:26, borderRadius:'50%', background:'rgba(0,212,170,.07)', border:'1px solid rgba(0,212,170,.18)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'var(--teal)' }}>{step.n}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:'var(--white)' }}>{step.label}</div>
                  <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:1 }}>{step.sub}</div>
                </div>
                <ChevronRight size={13} color="var(--slate-dim)" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* PRIMARY metric — tax bill */}
      <div className="card fade-up" style={{ marginBottom:16, animationDelay:'.04s' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div>
            <div style={{ fontSize:11, color:'var(--slate)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>Estimated tax bill — 2026/27</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:44, color:'var(--white)', lineHeight:1 }}>{fmt(total)}</div>
            <div style={{ fontSize:12, color:'var(--slate)', marginTop:6 }}>
              Set aside <strong style={{color:'var(--teal)'}}>{fmt(monthly)}/mo</strong> — updates as you categorise
            </div>
          </div>
          <span className="badge badge-amber">Projected</span>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
          {[
            { label:'Income',     value: fmt(income),            color:'var(--teal)' },
            { label:'Expenses',   value: fmt(totalExp),          color:'var(--white)' },
            { label:'Net profit', value: fmt(income - expenses), color: (income - expenses) > 0 ? 'var(--white)' : 'var(--red)' },
          ].map(s => (
            <div key={s.label} style={{ background:'rgba(255,255,255,.03)', borderRadius:8, padding:'10px 12px', border:'1px solid rgba(255,255,255,.05)' }}>
              <div style={{ fontSize:11, color:'var(--slate)', marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:16, fontWeight:600, color:s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display:'flex', justifyContent:'space-between', paddingTop:12, borderTop:'1px solid var(--navy-border)', flexWrap:'wrap', gap:8 }}>
          {[{ label:'Income Tax', val:incomeTax },{ label:'NI (Class 2 & 4)', val:ni }].map(r => (
            <div key={r.label} style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:12, color:'var(--slate)' }}>{r.label}</span>
              <span style={{ fontSize:12, fontWeight:600, color:'var(--white)' }}>{fmt(r.val)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Primary action */}
      <div className="fade-up" style={{ marginBottom:16, animationDelay:'.08s' }}>
        <Link to={primaryAction.link} style={{ textDecoration:'none' }}>
          <div style={{
            background: primaryAction.urgency === 'high' ? 'rgba(245,166,35,.05)' : primaryAction.urgency === 'low' ? 'rgba(0,212,170,.05)' : 'var(--navy-card)',
            border: `1px solid ${primaryAction.urgency === 'high' ? 'rgba(245,166,35,.25)' : primaryAction.urgency === 'low' ? 'rgba(0,212,170,.2)' : 'var(--navy-border)'}`,
            borderRadius:'var(--r)', padding:'14px 18px',
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
              <primaryAction.icon size={16} color={primaryAction.color} style={{ flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13, color:'var(--white)', marginBottom:2 }}>{primaryAction.label}</div>
                <div style={{ fontSize:12, color:'var(--slate)' }}>{primaryAction.sub}</div>
              </div>
            </div>
            <span className="btn btn-sm" style={{
              background: primaryAction.urgency === 'high' ? 'var(--amber)' : primaryAction.urgency === 'low' ? 'var(--teal)' : 'var(--navy-border)',
              color: primaryAction.urgency !== 'medium' ? 'var(--navy)' : 'var(--white)',
              border:'none', flexShrink:0, cursor:'pointer'
            }}>
              {primaryAction.cta} <ArrowRight size={12} />
            </span>
          </div>
        </Link>
      </div>

      {/* Two-col: checklist + chart */}
      <div className="g2 fade-up" style={{ marginBottom:18, animationDelay:'.12s' }}>
        <div className="card">
          <div style={{ fontWeight:600, fontSize:13, marginBottom:16, color:'var(--white)' }}>Your progress</div>
          <QuarterlyScore transactions={allTx} receiptsUploaded={receiptsUp} />
          <div className="div" />
          <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
            {[
              { label:'Transactions added',        done: profile?.bank_connected },
              { label:'All transactions reviewed', done: uncatCount === 0 && !isDemo },
              { label:'Receipts uploaded',         done: receiptsUp >= expCount * 0.5 && !isDemo },
              { label:'Ready to submit',           done: !isDemo && uncatCount === 0 && receiptsUp > 0 },
            ].map(item => (
              <div key={item.label} style={{ display:'flex', alignItems:'center', gap:9 }}>
                {item.done
                  ? <CheckCircle size={13} color="var(--teal)" />
                  : <div style={{ width:13, height:13, borderRadius:'50%', border:'2px solid var(--navy-border)', flexShrink:0 }} />
                }
                <span style={{ fontSize:12.5, color: item.done ? 'var(--white)' : 'var(--slate-dim)' }}>{item.label}</span>
              </div>
            ))}
          </div>
          <div className="div" />
          <Link to="/submit" className="btn btn-ghost btn-sm btn-full" style={{ justifyContent:'center' }}>
            Review and submit <ChevronRight size={13} />
          </Link>
        </div>

        <div className="card">
          <div style={{ fontWeight:600, fontSize:13, marginBottom:4, color:'var(--white)' }}>Profit trend</div>
          <div style={{ fontSize:12, color:'var(--slate)', marginBottom:16 }}>Income vs expenses, last 6 months</div>
          <div style={{ display:'flex', gap:14, marginBottom:14 }}>
            {[{color:'var(--teal)',label:'Income'},{color:'var(--slate)',label:'Expenses'}].map(l => (
              <div key={l.label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'var(--slate)' }}>
                <div style={{ width:7, height:7, borderRadius:'50%', background:l.color }} />{l.label}
              </div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={MONTHLY_DATA} margin={{ top:0, right:0, bottom:0, left:0 }}>
              <defs>
                <linearGradient id="ig" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#00D4AA" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#00D4AA" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="eg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3D5A72" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#3D5A72" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fill:'#7A9BB5', fontSize:10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill:'#7A9BB5', fontSize:10 }} axisLine={false} tickLine={false} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="income"   stroke="#00D4AA" strokeWidth={1.5} fill="url(#ig)" />
              <Area type="monotone" dataKey="expenses" stroke="#3D5A72" strokeWidth={1.5} fill="url(#eg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="card fade-up" style={{ animationDelay:'.16s' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ fontWeight:600, fontSize:13, color:'var(--white)' }}>Recent transactions</div>
          <Link to="/categorise" style={{ fontSize:12, color:'var(--teal)', textDecoration:'none' }}>View all</Link>
        </div>
        {allTx.slice(0,6).map(tx => {
          const cat = CATEGORIES.find(c => c.id === tx.category)
          return (
            <div key={tx.id} className="tx-row">
              <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                <div className="tx-icon" style={{ background: tx.type === 'income' ? 'rgba(0,212,170,.08)' : 'rgba(122,155,181,.08)', flexShrink:0 }}>
                  <span style={{ fontSize:11, fontWeight:700, color: tx.type==='income' ? 'var(--teal)' : 'var(--slate)' }}>
                    {tx.description.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.description}</div>
                  <div style={{ fontSize:11, color:'var(--slate-dim)' }}>{tx.date}</div>
                </div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color: tx.type==='income' ? 'var(--teal)' : 'var(--white)' }}>
                  {tx.type === 'income' ? '+' : '-'}{fmt(tx.amount)}
                </div>
                {tx.category
                  ? <div style={{ fontSize:11, color:'var(--slate-dim)' }}>{cat?.label || tx.category}</div>
                  : <span className="badge badge-amber" style={{ fontSize:10 }}>Needs review</span>
                }
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
